import { AfterViewInit, Component, ErrorHandler, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, maxLength, minLength, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'
import { Router } from '@angular/router'

import { LoginInputData, LoginService } from '../_angular/service/login.service'
import { VersionTarkistusPalvelu } from '../_angular/service/version-tarkistus.palvelu'

import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'

import { SaveNumberAndSendSmsRequest, SaveNumberAndSendSmsResponse } from '@lemon/core-firebase/model/login'

import { VaihdaKieliDialog, VaihdaKieliDialogData } from '../vaihda-kieli.dialog'

import { LemonaidValidators } from 'app/_angular/_validator/LemonaidValidators'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface PuhelinnumeroLomake {
  puhelin: string
}

@Component({
  templateUrl: './add-number.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddNumberComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatInput, { static: true }) puhelinInput: MatInput

  lomake = form(signal<PuhelinnumeroLomake>({ puhelin: '+358 ' }), polku => {
    required(polku.puhelin)
    minLength(polku.puhelin, 10)
    validoiReaktiivisesti(polku.puhelin, LemonaidValidators.phoneNumberValidator)
    maxLength(polku.puhelin, 20)
  })
  readonly commonError = signal<string | null>(null)
  online = true
  year = new Date().getFullYear()
  inputReceived: LoginInputData

  private ngUnsubscribe: Subject<void> = new Subject<void>()

  constructor(
    private _dialog: MatDialog,
    private _router: Router,
    private _validationService: FormValidationService,
    private _translationService: LemonTranslationService,
    private _versionTarkistaja: VersionTarkistusPalvelu,
    private _loginService: LoginService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _versionTarkistusPalvelu: VersionTarkistusPalvelu,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    // Preheat
    this._firebaseLemonaid.functionsCall<string, void>('loginTallennaPuhelinOngoingiinJaLahettaaSms', '')
  }

  ngOnInit(): void {
    this._loginService.currentLoginCredentialsObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(data => {
      this.inputReceived = data
      if (!this.inputReceived || !this.inputReceived.email || !this.inputReceived.password || !this.inputReceived.loginCodeId) {
        this.commonError.set(this._translationService.lokalisoi('login.login-out-of-date'))
      }
    })

    this._versionTarkistaja.sovelluksenVersioObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(versiotiedot => {
      this._versionTarkistaja.tarkistaVersio(versiotiedot)
    })
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.puhelinInput.focus()
    }, 0)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  setLoginProcessStarted() {
    this._ladataanService.aloitaLataaminen()
  }

  setLoginProcessEnded() {
    this._ladataanService.lopetaLataaminen()
  }

  back() {
    this._router.navigate(['/kirjaudu'])
  }

  @HostListener('document:keydown.enter')
  tallenna() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    this.setLoginProcessStarted()
    const puhelinData: SaveNumberAndSendSmsRequest = {
      email: this.inputReceived.email,
      password: this.inputReceived.password,
      puhelinnumero: String(this.lomake.puhelin().value()).replace(/\s/g, ''),
      loginCodeId: this.inputReceived.loginCodeId,
      valittuKieli: this._translationService.nykyinenKieli,
      env: 'w',
      ver: this._versionTarkistusPalvelu.annaVersio(),
      device: navigator.platform,
      os: navigator.userAgent,
    }

    this._firebaseLemonaid
      .functionsCall<SaveNumberAndSendSmsRequest, SaveNumberAndSendSmsResponse>('loginTallennaPuhelinOngoingiinJaLahettaaSms', puhelinData)
      .then(errorResponse => {
        if (!errorResponse) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          this._errorHandler.handleError('no-response-or-no-data')
          this.setLoginProcessEnded()
          return
        }

        if (errorResponse.e) {
          this.setLoginProcessEnded()
          if (errorResponse.e === 'auth/number-exists') {
            this.commonError.set(this._translationService.lokalisoi('lisaa-puhelin.number-exists'))
            return
          }
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          this._errorHandler.handleError(errorResponse.e)
        } else {
          this.setLoginProcessEnded()
          this._router.navigate(['/kirjaudu-vahvista'])
        }
      })
      .catch(err => {
        this.setLoginProcessEnded()
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        this._errorHandler.handleError(err)
      })
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data })
  }
}
